import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Download, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { downloadReceiptPdf, type ReceiptRecord } from '@/lib/receiptPdf';
import { toast } from 'sonner';

interface Props {
  /** Pass any one of these. */
  invoiceId?: string | null;
  receiptId?: string | null;
  /** Lookup latest receipt for a user+package (used for wallet purchase rows). */
  lookupByPackage?: { userId: string; packageId: string } | null;
  size?: 'sm' | 'default';
  variant?: 'default' | 'outline' | 'ghost' | 'secondary';
  label?: string;
  className?: string;
}

export function DownloadReceiptButton({
  invoiceId,
  receiptId,
  lookupByPackage,
  size = 'sm',
  variant = 'outline',
  label = 'PDF Receipt',
  className,
}: Props) {
  const [loading, setLoading] = useState(false);

  if (!invoiceId && !receiptId && !lookupByPackage) return null;

  const onClick = async () => {
    try {
      setLoading(true);
      let row: any = null;
      if (receiptId) {
        const { data, error } = await supabase
          .from('receipts' as any).select('*').eq('id', receiptId).maybeSingle();
        if (error) throw error;
        row = data;
      } else if (invoiceId) {
        const { data, error } = await supabase
          .from('receipts' as any).select('*').eq('invoice_id', invoiceId).maybeSingle();
        if (error) throw error;
        row = data;
      } else if (lookupByPackage) {
        const { data, error } = await supabase
          .from('receipts' as any).select('*')
          .eq('user_id', lookupByPackage.userId)
          .filter('metadata->>package_id', 'eq', lookupByPackage.packageId)
          .order('issued_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (error) throw error;
        row = data;
      }
      if (!row) {
        toast.error('Receipt not available yet for this purchase.');
        return;
      }
      await downloadReceiptPdf(row as ReceiptRecord);
    } catch (e: any) {
      toast.error('Could not download receipt: ' + (e?.message || 'unknown error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button size={size} variant={variant} className={className} onClick={onClick} disabled={loading}>
      {loading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1" />}
      {label}
    </Button>
  );
}
